interface HeroVideoInstance {
  root: HTMLElement;
  video: HTMLVideoElement;
  delay: number;
  started: boolean;
  revealed: boolean;
  retried: boolean;
  inView: boolean;
  priority: boolean;
  // Survives suspension; video.paused is stale on wake so it can never gate resume.
  wantsPlayback: boolean;
  resumeTimers: number[];
  resumeProbeTime: number;
}

const RESUME_DELAYS = [0, 400, 1000];

const LOAD_FALLBACK_MS = 3000;

const instances: HeroVideoInstance[] = [];
const byElement = new WeakMap<Element, HeroVideoInstance>();

let observer: IntersectionObserver | null = null;

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function isConstrainedConnection(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;

  if (!connection) {
    return false;
  }

  return Boolean(connection.saveData) || /(^|-)2g$/.test(connection.effectiveType ?? '');
}

function chooseSource(root: HTMLElement): string {
  const { src, srcMobile, src2x } = root.dataset;

  if (srcMobile && window.matchMedia('(max-width: 768px)').matches) {
    return srcMobile;
  }

  if (src2x && window.devicePixelRatio > 1.5) {
    return src2x;
  }

  return src ?? srcMobile ?? '';
}

function attemptPlay(instance: HeroVideoInstance): void {
  const { video } = instance;

  // bfcache can hand back an element with muted dropped, which blocks autoplay.
  video.muted = true;

  const played = video.play();
  if (played && typeof played.catch === 'function') {
    played.catch(() => {});
  }
}

function watchReveal(instance: HeroVideoInstance): void {
  const { root, video } = instance;

  // Below this the clip may still be on its blank opening frame.
  const minTime = instance.delay > 0 ? instance.delay : 0.05;

  const hasRevealableFrame = (): boolean => video.readyState >= 2 && video.currentTime >= minTime;

  const onProgress = (): void => {
    if (instance.revealed || !hasRevealableFrame()) {
      return;
    }

    instance.revealed = true;
    video.removeEventListener('timeupdate', onProgress);
    root.classList.add('is-video-ready');
  };

  video.addEventListener('timeupdate', onProgress);

  video.addEventListener(
    'playing',
    () => {
      window.setTimeout(
        () => {
          onProgress();

          if (instance.revealed || instance.retried) {
            return;
          }

          instance.retried = true;
          if (video.paused && 'visible' === document.visibilityState) {
            attemptPlay(instance);
          }
        },
        minTime * 1000 + 2000,
      );
    },
    { once: true },
  );
}

function start(instance: HeroVideoInstance): void {
  const { root, video } = instance;

  if (!instance.started) {
    const source = chooseSource(root);
    if (!source) {
      return;
    }

    instance.started = true;
    video.src = source;
  }

  instance.wantsPlayback = true;
  attemptPlay(instance);
}

function clearResumeTimers(instance: HeroVideoInstance): void {
  instance.resumeTimers.forEach((id) => window.clearTimeout(id));
  instance.resumeTimers = [];
}

function recoverSource(instance: HeroVideoInstance): void {
  const { video, root } = instance;

  // Only re-attach when the browser actually discarded the resource.
  if (video.networkState !== HTMLMediaElement.NETWORK_EMPTY) {
    return;
  }

  const source = chooseSource(root);
  if (!source) {
    return;
  }

  const resumeAt = video.currentTime;
  video.src = source;
  video.load();
  if (resumeAt > 0) {
    video.currentTime = resumeAt;
  }
}

function runResumeLadder(instance: HeroVideoInstance): void {
  clearResumeTimers(instance);
  instance.resumeProbeTime = -1;

  const attempt = (step: number): void => {
    if ('visible' !== document.visibilityState) {
      return;
    }

    const now = instance.video.currentTime;
    if (step > 0 && now > instance.resumeProbeTime + 0.01) {
      clearResumeTimers(instance);
      return;
    }
    instance.resumeProbeTime = now;

    recoverSource(instance);
    attemptPlay(instance);
  };

  attempt(0);
  RESUME_DELAYS.slice(1).forEach((delay, index) => {
    instance.resumeTimers.push(window.setTimeout(() => attempt(index + 1), delay));
  });
}

function markHidden(): void {
  instances.forEach(clearResumeTimers);
}

function resumeAll(): void {
  if ('visible' !== document.visibilityState) {
    return;
  }

  instances.forEach((instance) => {
    if (!instance.started || !instance.wantsPlayback) {
      return;
    }
    if (!instance.priority && !instance.inView) {
      return;
    }

    runResumeLadder(instance);
  });
}

function getObserver(): IntersectionObserver {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const instance = byElement.get(entry.target);
          if (!instance) {
            return;
          }

          instance.inView = entry.isIntersecting;

          if (entry.isIntersecting) {
            start(instance);
          } else if (instance.started) {
            instance.wantsPlayback = false;
            clearResumeTimers(instance);
            instance.video.pause();
          }
        });
      },
      { rootMargin: '200px 0px' },
    );
  }

  return observer;
}

function whenLoaded(callback: () => void): void {
  if ('complete' === document.readyState) {
    callback();
    return;
  }

  let fired = false;
  const run = (): void => {
    if (!fired) {
      fired = true;
      callback();
    }
  };

  // A stalled subresource can delay `load` indefinitely; never let that strand playback.
  window.addEventListener('load', run, { once: true });
  window.setTimeout(run, LOAD_FALLBACK_MS);
}

function init(root: HTMLElement): void {
  if (root.dataset.hvInit) {
    return;
  }
  root.dataset.hvInit = '1';

  const video = root.querySelector<HTMLVideoElement>('.wp-block-interact-hero-video__video');
  if (!video) {
    return;
  }

  if (prefersReducedMotion() || isConstrainedConnection()) {
    return;
  }

  const delay = Number(root.dataset.revealDelay ?? 0) || 0;
  const instance: HeroVideoInstance = {
    root,
    video,
    delay,
    started: false,
    revealed: false,
    retried: false,
    inView: true,
    priority: 'priority' === root.dataset.heroVideo,
    wantsPlayback: false,
    resumeTimers: [],
    resumeProbeTime: -1,
  };

  instances.push(instance);
  byElement.set(root, instance);
  watchReveal(instance);

  if ('priority' === root.dataset.heroVideo) {
    whenLoaded(() => start(instance));
    return;
  }

  if ('IntersectionObserver' in window) {
    instance.inView = false;
    getObserver().observe(root);
  } else {
    whenLoaded(() => start(instance));
  }
}

function bootstrap(): void {
  document
    .querySelectorAll<HTMLElement>('.wp-block-interact-hero-video[data-hero-video]')
    .forEach(init);

  if (!instances.length) {
    return;
  }

  document.addEventListener('visibilitychange', () => {
    if ('visible' === document.visibilityState) {
      resumeAll();
    } else {
      markHidden();
    }
  });
  window.addEventListener('pagehide', markHidden);
  window.addEventListener('pageshow', resumeAll);
  window.addEventListener('focus', resumeAll);
  window.addEventListener('orientationchange', resumeAll);

  ['pointerdown', 'touchstart'].forEach((type) =>
    window.addEventListener(type, resumeAll, { passive: true, once: true }),
  );
}

if ('loading' === document.readyState) {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}

// Module marker: stops top-level names colliding with other view scripts.
export {};
